import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, LogOut, Mail, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useBoundStore } from '../../store';
import { getOwnUser } from '../../services/User';
import { useI18n } from '../../i18n';


const SubscriptionExpiredPage: React.FC = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const accessToken = useBoundStore((state) => state.accessToken);
  const setAccessToken = useBoundStore((state) => state.setAccessToken);
  const setRefreshToken = useBoundStore((state) => state.setRefreshToken);
  const setUser = useBoundStore((state) => state.setUser);
  const user = useBoundStore((state) => state.user);
  const { t } = useI18n();
  const a = t.access;
  const code = user?.accessCode || 'default';
  const title = a.titles[code] ?? a.titles.default;
  const description = a.descriptions[code] ?? a.descriptions.default;

  // Re-check status on mount and whenever the tab regains focus, so an admin
  // extending the trial (or the user upgrading to a paid plan) lets them
  // back in without a manual log-out / log-in cycle.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    const recheck = () => {
      getOwnUser().then((res) => {
        if (cancelled || !res.success || !res.data) return;
        setUser(res.data);
        if (
          res.data.isExemptFromSubscription ||
          res.data.isSubscriptionActive
        ) {
          navigate('/', { replace: true });
        }
      });
    };
    recheck();
    window.addEventListener('focus', recheck);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', recheck);
    };
  }, [accessToken, setUser, navigate]);

  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText(a.contactEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = () => {
    setAccessToken('');
    setRefreshToken('');
    setUser(null);
    navigate('/');
  };

  const handleContactUs = () => {
    window.location.href = `mailto:${a.contactEmail}?subject=${encodeURIComponent(a.mailSubject)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-2xl">{title}</CardTitle>
          <CardDescription className="text-base mt-2">{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <span>{a.contactEmail}</span>
            <button
              onClick={handleCopyEmail}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title={a.copyEmail}
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
          <Button
            className="w-full"
            onClick={handleContactUs}
          >
            <Mail className="h-4 w-4 mr-2" />
            {a.contactUs}
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            {a.logOut}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionExpiredPage;
